import React, { useMemo, useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import {
  FaAngleRight,
  FaAngleLeft,
  FaAngleDoubleRight,
  FaAngleDoubleLeft,
} from 'react-icons/fa';
import Select from 'react-select';
import { selectStylesSm } from '~/Styles/reactSelectStyles';

// W-0627 — Dual-list de Grupos de Empresa (Ucapital.pas:583-596 — combo GEs + grupo)
// Fonte: ANALISE §2 reuso de pattern de seleção; PRD §6.2 layout dual-list.

const listaStyle = {
  border: '1px solid #ced4da',
  borderRadius: '4px',
  height: '260px',
  overflowY: 'auto',
  backgroundColor: '#fff',
  padding: '4px',
};

const itemStyle = {
  padding: '6px 10px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  userSelect: 'none',
  borderRadius: '3px',
};

const itemSelecionadoStyle = {
  backgroundColor: '#0747a6',
  color: '#fff',
};

const opcaoTodos = { value: '', label: 'Todos' };

const obterCodigosGrupo = (ge) => {
  const bruto = ge.codigos_grupos || ge.codigosGrupos;
  if (bruto) {
    return String(bruto)
      .split(',')
      .map((codigo) => Number(codigo))
      .filter((codigo) => !Number.isNaN(codigo));
  }
  if (ge.codigo_grupo == null && ge.codigoGrupo == null) return [];
  return [Number(ge.codigo_grupo == null ? ge.codigoGrupo : ge.codigo_grupo)];
};

const CarteiraDualList = ({
  gesDisponiveis,
  gesSelecionados,
  grupos,
  onChangeSelecionados,
}) => {
  const [grupoFiltro, setGrupoFiltro] = useState(opcaoTodos);
  const [foco, setFoco] = useState({ disponivel: null, selecionado: null });

  const opcoesGrupo = useMemo(
    () => [
      opcaoTodos,
      ...grupos.map((g) => ({
        value: g.codigo,
        label: `${g.codigo} - ${g.descricao}`,
      })),
    ],
    [grupos]
  );

  const selecionadosSet = useMemo(
    () => new Set(gesSelecionados.map((g) => g.codigo_grupo_empresa)),
    [gesSelecionados]
  );

  const filtroGrupoOperante = gesDisponiveis.some(
    (ge) => obterCodigosGrupo(ge).length > 0
  );

  const disponiveisFiltrados = useMemo(() => {
    const restantes = gesDisponiveis.filter(
      (ge) => !selecionadosSet.has(ge.codigo_grupo_empresa)
    );
    if (!filtroGrupoOperante) return restantes;
    if (!grupoFiltro || grupoFiltro.value === '') return restantes;
    return restantes.filter((ge) =>
      obterCodigosGrupo(ge).includes(Number(grupoFiltro.value))
    );
  }, [gesDisponiveis, selecionadosSet, grupoFiltro, filtroGrupoOperante]);

  const mover = (lista) => {
    const novosSelecionados = [...gesSelecionados, ...lista];
    onChangeSelecionados(novosSelecionados);
    setFoco({ disponivel: null, selecionado: null });
  };

  const remover = (codigos) => {
    const paraRemover = new Set(codigos);
    const restantes = gesSelecionados.filter(
      (g) => !paraRemover.has(g.codigo_grupo_empresa)
    );
    onChangeSelecionados(restantes);
    setFoco({ disponivel: null, selecionado: null });
  };

  const handleMoverUm = () => {
    if (foco.disponivel == null) return;
    const item = disponiveisFiltrados.find(
      (ge) => ge.codigo_grupo_empresa === foco.disponivel
    );
    if (item) mover([item]);
  };

  const handleRemoverUm = () => {
    if (foco.selecionado == null) return;
    remover([foco.selecionado]);
  };

  const handleMoverTodos = () => {
    if (disponiveisFiltrados.length === 0) return;
    mover(disponiveisFiltrados);
  };

  const handleRemoverTodos = () => {
    if (gesSelecionados.length === 0) return;
    onChangeSelecionados([]);
    setFoco({ disponivel: null, selecionado: null });
  };

  const renderItem = (ge, isSelecionado, ladoAtual) => {
    const ativo =
      ladoAtual === 'disponivel'
        ? foco.disponivel === ge.codigo_grupo_empresa
        : foco.selecionado === ge.codigo_grupo_empresa;
    return (
      <div
        key={ge.codigo_grupo_empresa}
        style={{ ...itemStyle, ...(ativo ? itemSelecionadoStyle : {}) }}
        onClick={() =>
          setFoco((f) => ({
            ...f,
            [ladoAtual]: ge.codigo_grupo_empresa,
          }))
        }
        onDoubleClick={() => (isSelecionado ? remover([ge.codigo_grupo_empresa]) : mover([ge]))}
      >
        {ge.codigo_grupo_empresa} - {ge.apelido}
      </div>
    );
  };

  return (
    <div>
      <Row className="mb-3">
        <Col xs={12} md={6} lg={4}>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-1">Grupo de GE (filtro)</Form.Label>
            <Select
              options={opcoesGrupo}
              value={grupoFiltro}
              onChange={(opt) => setGrupoFiltro(opt || opcaoTodos)}
              styles={selectStylesSm}
              isClearable={false}
              isDisabled={!filtroGrupoOperante}
              placeholder="Todos"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="align-items-center">
        <Col xs={12} md={5}>
          <Form.Label className="small mb-1">
            GEs Disponíveis ({disponiveisFiltrados.length})
          </Form.Label>
          <div style={listaStyle}>
            {disponiveisFiltrados.length === 0 ? (
              <div style={{ ...itemStyle, color: '#888' }}>
                Nenhum GE disponível
              </div>
            ) : (
              disponiveisFiltrados.map((ge) =>
                renderItem(ge, false, 'disponivel')
              )
            )}
          </div>
        </Col>

        <Col
          xs={12}
          md={2}
          className="d-flex flex-md-column flex-row justify-content-center align-items-center my-2 my-md-0"
        >
          <Button
            variant="outline-primary"
            size="sm"
            className="my-md-1 mx-1 mx-md-0"
            onClick={handleMoverUm}
            disabled={foco.disponivel == null}
            title="Mover selecionado"
          >
            <FaAngleRight />
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="my-md-1 mx-1 mx-md-0"
            onClick={handleMoverTodos}
            disabled={disponiveisFiltrados.length === 0}
            title="Mover todos"
          >
            <FaAngleDoubleRight />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="my-md-1 mx-1 mx-md-0"
            onClick={handleRemoverUm}
            disabled={foco.selecionado == null}
            title="Remover selecionado"
          >
            <FaAngleLeft />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="my-md-1 mx-1 mx-md-0"
            onClick={handleRemoverTodos}
            disabled={gesSelecionados.length === 0}
            title="Remover todos"
          >
            <FaAngleDoubleLeft />
          </Button>
        </Col>

        <Col xs={12} md={5}>
          <Form.Label className="small mb-1">
            GEs Selecionados ({gesSelecionados.length})
          </Form.Label>
          <div style={listaStyle}>
            {gesSelecionados.length === 0 ? (
              <div style={{ ...itemStyle, color: '#888' }}>
                Nenhum GE selecionado
              </div>
            ) : (
              gesSelecionados.map((ge) => renderItem(ge, true, 'selecionado'))
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CarteiraDualList;
